import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs/Rx';
import * as _ from 'lodash';
import * as moment from 'moment';

import * as fromRoot from '../store/reducers';
import { AppUiActions, TestTakerActions } from '../store/actions';
import { TestTakerService } from './test-taker.service';
import { ResponseService } from './response.service';
import { TestContentService } from './test-content.service';
import { MyModal } from '../components/common/my-modal/my-modal.component';
import { ConfigHelper } from './config.helper';
import * as Enum from '../dtos/enums';
import { TimerService } from './timer.service';
import { LoggerService } from './logger.service';
import * as ErrorMessages from '../errors/constants';

declare var _paragonConfig: PgRendererDto.ParagonAppConfig;

@Injectable()
export class TestNavigationService implements OnDestroy {

    shouldGoToNextTestlet$: Observable<boolean>;
    navigationInProgress$: Observable<Object>;
    testletHistory: PgRendererDto.TestletHistory[];
    testNavigation: PgRendererDto.TestNavigation;
    interactionComponentWithPreFlushProcessing: PgRendererDto.InteractionComponentForPreflush;
    private shouldGoToNextTestletSubscription: Subscription;
    private testNavigationSubscription: Subscription;
    private testletHistorySubscription: Subscription;
    private navigationInProgressSubscription: Subscription;
    private navigationInProgress = false;

    constructor(
        private store: Store<fromRoot.State>,
        private myModal: MyModal,
        private testTakerService: TestTakerService,
        private responseService: ResponseService,
        private testContentService: TestContentService,
        private timerService: TimerService,
        private loggerService: LoggerService
    ) {
        this.shouldGoToNextTestlet$ = this.store.select(fromRoot.shouldGoToNextTestlet);
        this.shouldGoToNextTestletSubscription = this.shouldGoToNextTestlet$.subscribe(shouldGoToNextTestlet => {
            if (shouldGoToNextTestlet) {
                this.store.dispatch(new TestTakerActions.ShouldGoToNextTestlet(false));
                this.dispatchTestNavigationNextAction();
            }
            this.store.dispatch(new AppUiActions.HideLoadingOverlay());
        });

        this.navigationInProgress$ = this.store.select(fromRoot.getIsNavigatingInProgress);
        this.navigationInProgressSubscription = this.navigationInProgress$.subscribe(navigationInProgress => {
            this.navigationInProgress = false;
        });

        this.testNavigationSubscription = store.select(fromRoot.getTestNavigation).subscribe(testNavigation => this.testNavigation = testNavigation);
        this.testletHistorySubscription = store.select(fromRoot.getTestletHistory).subscribe(testletHistory => this.testletHistory = testletHistory);
    }

    ngOnDestroy() {
        if (this.shouldGoToNextTestletSubscription) {
            this.shouldGoToNextTestletSubscription.unsubscribe();
        }
        if (this.testNavigationSubscription) {
            this.testNavigationSubscription.unsubscribe();
        }
        if (this.testletHistorySubscription) {
            this.testletHistorySubscription.unsubscribe();
        }
        if (this.navigationInProgressSubscription) {
            this.navigationInProgressSubscription.unsubscribe();
        }
    }

    goNext(shouldValidateResponses: boolean) {
        this.store.dispatch(new AppUiActions.HideLoadingOverlay());
        const nextItemIndex = this.getItemIndexInNextGroup();
        if (nextItemIndex) {
            const nextAction = () => this.changeAssessmentItem(nextItemIndex);
            if (shouldValidateResponses) {
                this.checkMinWordCountRequirement(nextAction);
            } else {
                nextAction();
            }
        } else {
            const nextAction = () => {
                if (!ConfigHelper.isPreviewMode()) {
                    if (ConfigHelper.isIppMode() && this.interactionComponentWithPreFlushProcessing) {
                        this.interactionComponentWithPreFlushProcessing.runPreFlushProcessing();
                    }
                    this.responseService.flush(false)
                        .catch(() => {
                            this.store.dispatch(new AppUiActions.HideLoadingOverlay());
                            this.myModal.showRegularDlg("Error", ErrorMessages.Navigation_Response_Saving);
                        });
                } else {
                    this.dispatchTestNavigationNextAction();
                }
            };
            if (shouldValidateResponses) {
                this.checkMinWordCountRequirement(nextAction);
            } else {
                nextAction();
            }
        }
    }

    getTestlets(assessmentTest: PgRendererDto.AssessmentTest) {
        return [].concat.apply([], assessmentTest.TestParts.map((p, testPartIndex) => p.AssessmentSectionRefs.map((x, testletIndex) => {
            return {
                name: x.Href,
                testletId: x.TestletId,
                title: x.PageHeader ? x.PageHeader : x.Href,
                testNavigation: {
                    dateAdded: new Date(),
                    testPartIndex: testPartIndex,
                    testletIndex: testletIndex,
                    canGoNext: true,
                    isEndOfTest: false
                },
                isSelected: testPartIndex === 0 && testletIndex === 0
            };
        })));
    }

    getTestletsByTestPart(testPart: PgRendererDto.TestPart, testPartIndex: number) {
        return [].concat.apply([], testPart.AssessmentSectionRefs.map((x, testletIndex) => {
            return {
                name: x.Href,
                testletId: x.TestletId,
                title: x.PageHeader ? x.PageHeader : x.Href,
                testNavigation: {
                    testPartIndex: testPartIndex,
                    testletIndex: testletIndex,
                    canGoNext: true,
                    isEndOfTest: false
                },
                isSelected: testPartIndex === 0 && testletIndex === 0
            };
        }));
    }

    private checkMinWordCountRequirement(executeNextCallback: Function) {
        let meetMinWordCountRequirement = true;
        const activeAssessmentItem = this.testContentService.activeAssessmentItem;
        const referencedInteractions = this.testContentService.getReferencedInteractionsByItemGroup(this.testContentService.activeTestlet, activeAssessmentItem.GroupNumber);
        const extendedTextInteraction = referencedInteractions.find(x => x.ExtendedTextInteraction != null);
        if (extendedTextInteraction) {
            const response = this.responseService.getResponse(extendedTextInteraction);
            meetMinWordCountRequirement = this.responseService.meetMinWordCountRequirement(extendedTextInteraction.ExtendedTextInteraction, response);
        }

        if (!meetMinWordCountRequirement) {
            const keepWorkingLabel = "Keep working";
            this.myModal.showRegularDlg("Confirmation",
                "You have not written the minimum recommended number of words. Would you like to move on?",
                [
                    { label: "No", style: 'btn-success' },
                    { label: "Yes", style: '' }
                ])
                .then(button => {
                    if (button != "No") {
                        this.testTakerService.cacheTestTakerActivity(Enum.TestTakerActivityTypeEnum.IgnoreMinWordCountWarningYes, null, null, this.testContentService.activeTestletId);
                        if (referencedInteractions.length == 1) {
                            this.dispatchTestNavigationNextAction();
                        } else {
                            this.checkForUnansweredInteractions(executeNextCallback);
                        }
                    } else {
                        this.testTakerService.cacheTestTakerActivity(Enum.TestTakerActivityTypeEnum.IgnoreMinWordCountWarningNo, null, null, this.testContentService.activeTestletId);
                    }
                });
        } else {
            if (extendedTextInteraction != null) {
                this.showConfirmationDialog(executeNextCallback);
            } else {
                this.checkForUnansweredInteractions(executeNextCallback);
            }
        }
    }

    private showConfirmationDialog(executeNextCallback: Function) {
        const skipLabel = "Skip and go to the next page";
        this.myModal.showRegularDlg("Confirmation",
            "Make sure you have completed this task as you cannot come back after going to the next page.",
            [
                { label: "Keep working", style: 'btn-success' },
                { label: skipLabel, style: '' }
            ])
            .then(button => {
                if (button == skipLabel) {
                    this.checkForUnansweredInteractions(executeNextCallback);
                }
            });
    }

    private checkForUnansweredInteractions(executeNextCallback: Function) {
        const unansweredInteractionCount = this.responseService.getUnansweredInteractionCountInItemGroup(this.testContentService.activeTestlet, this.testContentService.activeAssessmentItem.GroupNumber);
        if (unansweredInteractionCount > 0) {
            const keepWorkingLabel = "Keep working";
            this.myModal.showRegularDlg("Confirmation",
                "You have not answered all the questions. If you go to the next page, you will no longer be able to answer these questions.",
                [
                    { label: keepWorkingLabel, style: 'btn-success' },
                    { label: 'Skip and go to the next page', style: '' }
                ])
                .then(button => {
                    if (button != keepWorkingLabel) {
                        this.testTakerService.cacheTestTakerActivity(Enum.TestTakerActivityTypeEnum.IgnoreUnansweredInteractionWarningYes, null, null, this.testContentService.activeTestletId);
                        executeNextCallback();
                    } else {
                        this.testTakerService.cacheTestTakerActivity(Enum.TestTakerActivityTypeEnum.IgnoreUnansweredInteractionWarningNo, null, null, this.testContentService.activeTestletId);
                    }
                });
        } else {
            executeNextCallback();
        }
    }

    changeAssessmentItem(index: number) {
        this.store.dispatch(new TestTakerActions.ToggleTimerVisibility(true));
        this.store.dispatch(new TestTakerActions.SetItemIndex(index));
    }

    private getItemIndexInNextGroup(): number {
        const currentTestlet = this.testContentService.activeTestlet;
        const activeAssessmentItem = this.testContentService.activeAssessmentItem;
        if (activeAssessmentItem && !this.isLastAssessmentItemGroup(activeAssessmentItem.GroupNumber, currentTestlet)) {
            return activeAssessmentItem.ItemIndex + 1;
        }
        return null;
    }

    setCanGoNext(canGoNext: boolean) {
        const testTakerInfo = this.testTakerService.testTakerInfo;
        if (ConfigHelper.shouldAlwaysHideNextButton()) {
            this.store.dispatch(new TestTakerActions.SetCanGoNext(false));
        } else {
            this.store.dispatch(new TestTakerActions.SetCanGoNext(canGoNext));
        }
    }

    indicateEndOfTest(testletId: number) {
        this.testTakerService.saveTestTakerActivityWithoutCaching(Enum.TestTakerActivityTypeEnum.LeaveTestlet, null, null, testletId);
        this.testTakerService.saveTestTakerActivityWithoutCaching(Enum.TestTakerActivityTypeEnum.TestEnd);
        this.store.dispatch(new TestTakerActions.IndicateEndOfTest());
    }

    isLastAssessmentItemGroup(groupNumber: number, testlet: PgRendererDto.TestletDefinition): boolean {
        return groupNumber >= _.max(testlet.AssessmentItems.map(x => x.GroupNumber));
    }

    private dispatchTestNavigationNextAction() {
        if (this.shouldDiscardNavigationNextAction()) {
            return;
        }
        this.navigationInProgress = true;
        if (!this.timerService.isPaused()) {
            this.timerService.pauseTimer();
        }
        const testPartIndex = this.testNavigation ? this.testNavigation.testPartIndex : 0;
        const testletIndex = this.testNavigation ? this.testNavigation.testletIndex : 0;
        this.store.dispatch(new TestTakerActions.TestNavigationNext(testPartIndex, testletIndex));
    }

    private shouldDiscardNavigationNextAction(): boolean {
        if (ConfigHelper.isTdpMode()) {
            const now = moment(new Date());
            const testNavigationDateAdded = moment(this.testNavigation.dateAdded);
            const duration = moment.duration(now.diff(testNavigationDateAdded)).asSeconds();
            return this.navigationInProgress || duration < 15;
        } else {
            return this.navigationInProgress;
        }
    }

    private onUnload(event) {
        event.preventDefault();
        event.returnValue = '';
    }

    enableWindowUnloadingWarning() {
        window.addEventListener('beforeunload', this.onUnload);
    }

    disableWindowUnloadingWarning() {
        window.removeEventListener('beforeunload', this.onUnload);
    }
}



// WEBPACK FOOTER //
// C:/_/Platform1/Paragon/Application/Common/UI.RenderEngine/src/app/services/test-navigation.service.ts